import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  User,
  Lock,
  Mail,
  X,
  Check,
  LogOut,
  ArrowRight,
  UserPlus,
  AlertTriangle,
  Palette
} from 'lucide-react';
import {
  loginUserSession,
  registerUserSession,
  updateUserProfile,
  fetchUserProfile,
  UserProfile
} from '../../services/vaultService';
import {
  CharacterAvatar,
  CHARACTER_PRESETS,
  CharacterPreset
} from './CharacterAvatar';

interface UserSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUserName: string;
  onSaveUser: (userName: string) => void;
  onAvatarChange?: (avatarUrl: string) => void;
}

export const UserSessionModal: React.FC<UserSessionModalProps> = ({
  isOpen,
  onClose,
  currentUserName,
  onSaveUser,
  onAvatarChange
}) => {
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const [isViewingProfile, setIsViewingProfile] = useState<boolean>(!!currentUserName);
  const [nameInput, setNameInput] = useState<string>(currentUserName);
  const [passwordInput, setPasswordInput] = useState<string>('');
  const [emailInput, setEmailInput] = useState<string>('');
  const [selectedAvatarId, setSelectedAvatarId] = useState<string>('char-maverick');
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState<boolean>(false);

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isNotRegistered, setIsNotRegistered] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [successMsg, setSuccessMsg] = useState<string>('');

  const handleCloseAvatarModal = () => {
    setIsAvatarModalOpen(false);
    const chosen = selectedAvatarId || profile?.avatarUrl || 'char-maverick';
    if (onAvatarChange) {
      onAvatarChange(chosen);
    }
  };

  // Handle ESC key to dismiss modal or avatar popup
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isAvatarModalOpen) {
          handleCloseAvatarModal();
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isAvatarModalOpen, profile?.avatarUrl, selectedAvatarId, onClose]);

  // Fetch live D1 profile whenever modal opens with an active user
  useEffect(() => {
    if (isOpen) {
      setErrorMsg(null);
      setIsNotRegistered(false);
      setIsSuccess(false);
      setPasswordInput('');
      setEmailInput('');
      setIsAvatarModalOpen(false);

      if (currentUserName) {
        setIsViewingProfile(true);
        setNameInput(currentUserName);
        setIsLoadingProfile(true);
        fetchUserProfile(currentUserName)
          .then((p) => {
            if (p) {
              setProfile(p);
              if (p.avatarUrl) {
                setSelectedAvatarId(p.avatarUrl);
                if (onAvatarChange) onAvatarChange(p.avatarUrl);
              }
              if (p.email) setEmailInput(p.email);
            }
          })
          .finally(() => setIsLoadingProfile(false));
      } else {
        setIsViewingProfile(false);
        setAuthMode('login');
        setNameInput('');
        setSelectedAvatarId('char-maverick');
        setProfile(null);
      }
    }
  }, [isOpen, currentUserName]);

  if (!isOpen || typeof document === 'undefined') return null;

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const clean = nameInput.trim();
    if (!clean) return;

    const cleanPassword = passwordInput.trim();
    if (!cleanPassword) {
      setErrorMsg('Password is required to sign in.');
      return;
    }

    setErrorMsg(null);
    setIsNotRegistered(false);
    setIsSubmitting(true);

    try {
      const result = await loginUserSession(clean, cleanPassword);
      if (result.success && result.user) {
        setProfile(result.user);
        onSaveUser(result.user.username);
        if (onAvatarChange && result.user.avatarUrl) {
          onAvatarChange(result.user.avatarUrl);
        }
        setIsSuccess(true);
        setSuccessMsg(`Welcome back, ${result.user.username}!`);
        setTimeout(() => {
          onClose();
        }, 500);
      } else {
        setErrorMsg(result.error || 'Failed to sign in.');
        if (result.notRegistered) {
          setIsNotRegistered(true);
        }
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Error communicating with server.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const clean = nameInput.trim();
    if (!clean) return;

    const cleanEmail = emailInput.trim();
    if (!cleanEmail) {
      setErrorMsg('Email address is required to register.');
      return;
    }

    const cleanPassword = passwordInput.trim();
    if (!cleanPassword) {
      setErrorMsg('Password is required to register.');
      return;
    }

    setErrorMsg(null);
    setIsNotRegistered(false);
    setIsSubmitting(true);

    try {
      const result = await registerUserSession(clean, cleanPassword, {
        email: cleanEmail,
        avatarUrl: selectedAvatarId
      });

      if (result.success && result.user) {
        setProfile(result.user);
        onSaveUser(result.user.username);
        if (onAvatarChange && result.user.avatarUrl) {
          onAvatarChange(result.user.avatarUrl);
        }
        setIsSuccess(true);
        setSuccessMsg(`Performer registered! Welcome, ${clean}!`);
        setTimeout(() => {
          onClose();
        }, 600);
      } else {
        setErrorMsg(result.error || 'Registration failed.');
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Error communicating with server.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAvatarChange = (avatarId: string) => {
    setSelectedAvatarId(avatarId);
    setProfile((prev) => (prev ? { ...prev, avatarUrl: avatarId } : prev));
    if (onAvatarChange) {
      onAvatarChange(avatarId);
    }
    if (profile?.username) {
      updateUserProfile(profile.username, { avatarUrl: avatarId })
        .catch((err) => {
          console.warn('Avatar update notice:', err);
        });
    }
  };

  const handleLogout = () => {
    onSaveUser('');
    setProfile(null);
    setNameInput('');
    setPasswordInput('');
    setEmailInput('');
    setIsViewingProfile(false);
    setAuthMode('login');
    onClose();
  };

  const switchToRegisterWithCurrentName = () => {
    setAuthMode('register');
    setErrorMsg(null);
    setIsNotRegistered(false);
  };

  return createPortal(
    <>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 99999,
          background: 'rgba(5, 8, 20, 0.85)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1.5rem',
          overflowY: 'auto'
        }}
        onClick={onClose}
      >
      <div
        style={{
          width: '100%',
          maxWidth: '460px',
          background: 'linear-gradient(145deg, rgba(20, 27, 45, 0.98) 0%, rgba(10, 15, 30, 0.99) 100%)',
          border: '1px solid rgba(139, 92, 246, 0.35)',
          borderRadius: 'var(--radius-lg, 16px)',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.8), 0 0 35px rgba(139, 92, 246, 0.25)',
          padding: '1.75rem',
          position: 'relative',
          margin: 'auto',
          maxHeight: 'calc(100vh - 4rem)',
          overflowY: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '1.1rem',
            right: '1.1rem',
            background: 'rgba(255, 255, 255, 0.06)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            color: 'var(--text-muted, #94a3b8)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.15s ease'
          }}
          title="Close"
        >
          <X size={16} />
        </button>

        {/* ── MODE A: VIEW CURRENT LOGGED IN PROFILE ── */}
        {isViewingProfile && currentUserName ? (
          <div>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '1.25rem' }}>
              <CharacterAvatar
                avatarId={profile?.avatarUrl || selectedAvatarId}
                fallbackInitial={currentUserName.charAt(0)}
                size="lg"
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#ffffff', margin: 0 }}>
                  {currentUserName}
                </h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted, #94a3b8)' }}>
                    {profile?.email ? profile.email : 'No email attached'}
                  </span>
                  <button
                    type="button"
                    onClick={() => setIsAvatarModalOpen(true)}
                    style={{
                      background: 'rgba(139, 92, 246, 0.2)',
                      border: '1px solid rgba(168, 85, 247, 0.4)',
                      borderRadius: '4px',
                      color: '#d8b4fe',
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      padding: '2px 7px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <Palette size={11} />
                    <span>Change Avatar</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Profile Action Buttons: Sign Out only */}
            <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button
                type="button"
                onClick={handleLogout}
                style={{
                  width: '100%',
                  padding: '0.7rem 1.25rem',
                  background: 'rgba(239, 68, 68, 0.12)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: 'var(--radius-md, 8px)',
                  color: '#fca5a5',
                  fontWeight: 700,
                  fontSize: '0.86rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.15s ease'
                }}
              >
                <LogOut size={16} />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        ) : (
          /* ── MODE B: LOGIN / REGISTER FORMS ── */
          <div>
            {/* Modal Header Tabs */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1.25rem', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '0.85rem' }}>
              <button
                type="button"
                onClick={() => {
                  setAuthMode('login');
                  setErrorMsg(null);
                  setIsNotRegistered(false);
                }}
                style={{
                  padding: '6px 14px',
                  borderRadius: '6px',
                  border: 'none',
                  background: authMode === 'login' ? 'rgba(139, 92, 246, 0.25)' : 'transparent',
                  color: authMode === 'login' ? '#ffffff' : 'var(--text-muted, #94a3b8)',
                  fontWeight: 700,
                  fontSize: '0.88rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: authMode === 'login' ? '0 0 12px rgba(139, 92, 246, 0.3)' : 'none'
                }}
              >
                <User size={16} />
                <span>Sign In</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setAuthMode('register');
                  setErrorMsg(null);
                  setIsNotRegistered(false);
                }}
                style={{
                  padding: '6px 14px',
                  borderRadius: '6px',
                  border: 'none',
                  background: authMode === 'register' ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.3) 0%, rgba(5, 150, 105, 0.3) 100%)' : 'transparent',
                  color: authMode === 'register' ? '#a7f3d0' : 'var(--text-muted, #94a3b8)',
                  fontWeight: 700,
                  fontSize: '0.88rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: authMode === 'register' ? '0 0 12px rgba(16, 185, 129, 0.3)' : 'none'
                }}
              >
                <UserPlus size={16} />
                <span>Register</span>
              </button>
            </div>

            {/* Title & Subtitle */}
            <div style={{ marginBottom: '1.25rem' }}>
              <h2 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#ffffff', margin: 0 }}>
                {authMode === 'login' ? 'Performer Sign In' : 'Register New Performer'}
              </h2>
              <p style={{ margin: '4px 0 0', fontSize: '0.78rem', color: 'var(--text-muted, #94a3b8)' }}>
                {authMode === 'login'
                  ? 'Only registered performers in D1 can sign in.'
                  : 'Create your performer account in Cloudflare D1 with a custom character.'}
              </p>
            </div>

            {/* Error Alert Box */}
            {errorMsg && (
              <div
                style={{
                  padding: '0.75rem 1rem',
                  borderRadius: '8px',
                  background: 'rgba(239, 68, 68, 0.15)',
                  border: '1px solid rgba(239, 68, 68, 0.4)',
                  color: '#fca5a5',
                  fontSize: '0.8rem',
                  marginBottom: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700 }}>
                  <AlertTriangle size={16} color="#ef4444" />
                  <span>{errorMsg}</span>
                </div>

                {isNotRegistered && (
                  <button
                    type="button"
                    onClick={switchToRegisterWithCurrentName}
                    style={{
                      alignSelf: 'flex-start',
                      marginTop: '4px',
                      padding: '4px 10px',
                      background: 'rgba(16, 185, 129, 0.25)',
                      border: '1px solid rgba(16, 185, 129, 0.5)',
                      borderRadius: '4px',
                      color: '#6ee7b7',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <UserPlus size={13} />
                    <span>Register "{nameInput}" now →</span>
                  </button>
                )}
              </div>
            )}

            {/* Success Alert Box */}
            {isSuccess && (
              <div
                style={{
                  padding: '0.75rem 1rem',
                  borderRadius: '8px',
                  background: 'rgba(16, 185, 129, 0.2)',
                  border: '1px solid rgba(16, 185, 129, 0.5)',
                  color: '#6ee7b7',
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Check size={16} />
                <span>{successMsg}</span>
              </div>
            )}

            {/* Form */}
            <form
              onSubmit={authMode === 'login' ? handleLoginSubmit : handleRegisterSubmit}
              style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
            >
              {/* Username Input */}
              <div>
                <label
                  htmlFor="session-user-name"
                  style={{
                    display: 'block',
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    color: '#e2e8f0',
                    marginBottom: '6px'
                  }}
                >
                  Performer Username <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <User
                    size={16}
                    color="var(--text-muted, #94a3b8)"
                    style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }}
                  />
                  <input
                    id="session-user-name"
                    type="text"
                    value={nameInput}
                    onChange={(e) => {
                      setNameInput(e.target.value);
                      if (errorMsg) {
                        setErrorMsg(null);
                        setIsNotRegistered(false);
                      }
                    }}
                    placeholder={authMode === 'login' ? 'e.g. Dan, Sasa...' : 'e.g. Elena, SoloVoice...'}
                    autoFocus
                    required
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      background: 'rgba(15, 23, 42, 0.75)',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      borderRadius: 'var(--radius-md, 8px)',
                      padding: '0.65rem 0.85rem 0.65rem 2.4rem',
                      color: '#ffffff',
                      fontSize: '0.9rem',
                      outline: 'none',
                      transition: 'border-color 0.15s ease'
                    }}
                    onFocus={(e) => (e.target.style.borderColor = 'var(--accent-purple, #8b5cf6)')}
                    onBlur={(e) => (e.target.style.borderColor = 'rgba(255, 255, 255, 0.15)')}
                  />
                </div>
              </div>

              {/* Character Avatar Picker (In Register mode) */}
              {authMode === 'register' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#e2e8f0' }}>
                      Choose Your Character Avatar
                    </label>
                    <span style={{ fontSize: '0.7rem', color: '#38bdf8' }}>9 Characters</span>
                  </div>

                  {/* Character Grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '8px' }}>
                    {CHARACTER_PRESETS.map((preset) => {
                      const isSelected = selectedAvatarId === preset.id;
                      return (
                        <div
                          key={preset.id}
                          onClick={() => setSelectedAvatarId(preset.id)}
                          style={{
                            background: isSelected ? 'rgba(139, 92, 246, 0.25)' : 'rgba(15, 23, 42, 0.7)',
                            border: isSelected ? `2px solid ${preset.color}` : '1px solid rgba(255, 255, 255, 0.12)',
                            borderRadius: '10px',
                            padding: '10px 6px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '6px',
                            cursor: 'pointer',
                            boxShadow: isSelected ? `0 0 16px ${preset.borderColor}` : 'none',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          <CharacterAvatar avatarId={preset.id} size="md" />
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#ffffff' }}>
                              {preset.name}
                            </div>
                            <div style={{ fontSize: '0.66rem', color: 'var(--text-muted, #94a3b8)' }}>
                              {preset.tagline}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Mandatory Email Input (In Register mode) */}
              {authMode === 'register' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <label
                      htmlFor="session-email"
                      style={{ fontSize: '0.78rem', fontWeight: 700, color: '#e2e8f0' }}
                    >
                      Email Address <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <Mail
                      size={16}
                      color="var(--text-muted, #94a3b8)"
                      style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }}
                    />
                    <input
                      id="session-email"
                      type="email"
                      required
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      placeholder="performer@example.com"
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        background: 'rgba(15, 23, 42, 0.75)',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        borderRadius: 'var(--radius-md, 8px)',
                        padding: '0.65rem 0.85rem 0.65rem 2.4rem',
                        color: '#ffffff',
                        fontSize: '0.9rem',
                        outline: 'none',
                        transition: 'border-color 0.15s ease'
                      }}
                      onFocus={(e) => (e.target.style.borderColor = 'var(--accent-purple, #8b5cf6)')}
                      onBlur={(e) => (e.target.style.borderColor = 'rgba(255, 255, 255, 0.15)')}
                    />
                  </div>
                </div>
              )}

              {/* Password Input (Mandatory in both login and register) */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <label
                    htmlFor="session-user-password"
                    style={{ fontSize: '0.78rem', fontWeight: 700, color: '#e2e8f0' }}
                  >
                    Password <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                </div>
                <div style={{ position: 'relative' }}>
                  <Lock
                    size={16}
                    color="var(--text-muted, #94a3b8)"
                    style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }}
                  />
                  <input
                    id="session-user-password"
                    type="password"
                    required
                    value={passwordInput}
                    onChange={(e) => setPasswordInput(e.target.value)}
                    placeholder="••••••••"
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      background: 'rgba(15, 23, 42, 0.75)',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      borderRadius: 'var(--radius-md, 8px)',
                      padding: '0.65rem 0.85rem 0.65rem 2.4rem',
                      color: '#ffffff',
                      fontSize: '0.9rem',
                      outline: 'none',
                      transition: 'border-color 0.15s ease'
                    }}
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ marginTop: '0.5rem' }}>
                {authMode === 'login' ? (
                  <button
                    type="submit"
                    disabled={!nameInput.trim() || !passwordInput.trim() || isSubmitting}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1.25rem',
                      background: isSuccess
                        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                        : 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                      border: 'none',
                      borderRadius: 'var(--radius-md, 8px)',
                      color: '#ffffff',
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      cursor: nameInput.trim() && passwordInput.trim() && !isSubmitting ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      boxShadow: '0 4px 16px rgba(139, 92, 246, 0.4)',
                      opacity: nameInput.trim() && passwordInput.trim() && !isSubmitting ? 1 : 0.6,
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {isSubmitting ? (
                      <span>Signing In...</span>
                    ) : isSuccess ? (
                      <>
                        <Check size={16} />
                        <span>Signed In!</span>
                      </>
                    ) : (
                      <>
                        <span>Sign In</span>
                        <ArrowRight size={15} />
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={!nameInput.trim() || !emailInput.trim() || !passwordInput.trim() || isSubmitting}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1.25rem',
                      background: isSuccess
                        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                        : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      border: 'none',
                      borderRadius: 'var(--radius-md, 8px)',
                      color: '#ffffff',
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      cursor: nameInput.trim() && emailInput.trim() && passwordInput.trim() && !isSubmitting ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      boxShadow: '0 4px 16px rgba(16, 185, 129, 0.4)',
                      opacity: nameInput.trim() && emailInput.trim() && passwordInput.trim() && !isSubmitting ? 1 : 0.6,
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {isSubmitting ? (
                      <span>Registering...</span>
                    ) : isSuccess ? (
                      <>
                        <Check size={16} />
                        <span>Registered!</span>
                      </>
                    ) : (
                      <>
                        <UserPlus size={16} />
                        <span>Register</span>
                      </>
                    )}
                  </button>
                )}
              </div>

              {/* Mode Toggle Footer */}
              <div style={{ textAlign: 'center', marginTop: '0.25rem', fontSize: '0.78rem', color: 'var(--text-muted, #94a3b8)' }}>
                {authMode === 'login' ? (
                  <span>
                    New performer?{' '}
                    <button
                      type="button"
                      onClick={() => {
                        setAuthMode('register');
                        setErrorMsg(null);
                        setIsNotRegistered(false);
                      }}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#a7f3d0',
                        fontWeight: 700,
                        cursor: 'pointer',
                        padding: 0,
                        textDecoration: 'underline'
                      }}
                    >
                      Register here
                    </button>
                  </span>
                ) : (
                  <span>
                    Already registered?{' '}
                    <button
                      type="button"
                      onClick={() => {
                        setAuthMode('login');
                        setErrorMsg(null);
                        setIsNotRegistered(false);
                      }}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#c084fc',
                        fontWeight: 700,
                        cursor: 'pointer',
                        padding: 0,
                        textDecoration: 'underline'
                      }}
                    >
                      Sign In here
                    </button>
                  </span>
                )}
              </div>
            </form>
          </div>
        )}
      </div>
    </div>

    {/* ── AVATAR SELECTION POPUP MODAL (Clean unconstrained top-level portal) ── */}
    {isAvatarModalOpen && (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 100005,
          background: 'rgba(2, 4, 12, 0.92)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem',
          overflowY: 'auto'
        }}
        onClick={handleCloseAvatarModal}
      >
        <div
          style={{
            width: '100%',
            maxWidth: '460px',
            maxHeight: 'calc(100vh - 2.5rem)',
            margin: 'auto',
            overflowY: 'auto',
            background: 'linear-gradient(145deg, #141b2d 0%, #0a0f1e 100%)',
            border: '1px solid rgba(139, 92, 246, 0.5)',
            borderRadius: '16px',
            boxShadow: '0 30px 70px rgba(0, 0, 0, 0.95), 0 0 35px rgba(139, 92, 246, 0.3)',
            padding: '1.5rem',
            position: 'relative',
            color: '#ffffff',
            transform: 'translateZ(0)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: '#ffffff' }}>
                Choose Your Avatar
              </h3>
              <p style={{ margin: '3px 0 0 0', fontSize: '0.78rem', color: 'var(--text-muted, #94a3b8)' }}>
                Select a performer character for your profile
              </p>
            </div>
            <button
              type="button"
              onClick={handleCloseAvatarModal}
              style={{
                background: 'rgba(255, 255, 255, 0.06)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                color: 'var(--text-muted, #94a3b8)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Close"
            >
              <X size={16} />
            </button>
          </div>

          {/* 3x3 Grid of 9 Avatars */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '1.25rem' }}>
            {CHARACTER_PRESETS.map((preset) => {
              const currentActive = selectedAvatarId || profile?.avatarUrl || 'char-maverick';
              const isSelected = currentActive === preset.id;
              return (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => handleAvatarChange(preset.id)}
                  style={{
                    background: isSelected ? 'rgba(139, 92, 246, 0.28)' : 'rgba(255, 255, 255, 0.04)',
                    border: isSelected ? `2px solid ${preset.color}` : '1px solid rgba(255, 255, 255, 0.12)',
                    borderRadius: '12px',
                    padding: '10px 4px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    boxShadow: isSelected ? `0 0 16px ${preset.borderColor}` : 'none',
                    position: 'relative',
                    outline: 'none',
                    userSelect: 'none'
                  }}
                >
                  <CharacterAvatar avatarId={preset.id} size="md" />
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 700, color: isSelected ? '#ffffff' : '#e2e8f0' }}>
                      {preset.name}
                    </div>
                    <div style={{ fontSize: '0.68rem', color: isSelected ? '#d8b4fe' : 'var(--text-muted, #94a3b8)', marginTop: '2px' }}>
                      {preset.tagline}
                    </div>
                  </div>
                  {isSelected && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '6px',
                        right: '6px',
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        background: '#22c55e',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Check size={11} color="#ffffff" strokeWidth={3} />
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Confirm / Close button */}
          <button
            type="button"
            onClick={handleCloseAvatarModal}
            style={{
              width: '100%',
              padding: '0.65rem',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
              border: 'none',
              borderRadius: 'var(--radius-md, 8px)',
              color: '#ffffff',
              fontWeight: 700,
              fontSize: '0.88rem',
              cursor: 'pointer'
            }}
          >
            Done
          </button>
        </div>
      </div>
    )}
  </>,
  document.body
);
};
